import React from 'react';
import { Phone, Mail, MapPin, Facebook, Instagram, ArrowUp, Linkedin, Search } from 'lucide-react';
import { companyInfo } from '../data/mock';

export const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (event, sectionId) => {
    event.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="text-2xl font-bold">
              <span className="text-blue-400">IN SMART</span> MEDIA
            </div>
            <p className="text-gray-400 leading-relaxed">
              Your complete advertising & branding solution. Delivering excellence for over 17 years.
            </p>
            <div className="flex gap-4">
              <a 
                href={companyInfo.socialMedia.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 p-3 rounded-full hover:bg-blue-600 transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a 
                href={companyInfo.socialMedia.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 p-3 rounded-full hover:bg-blue-600 transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="https://www.linkedin.com/company/in-smart-media/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 p-3 rounded-full hover:bg-blue-600 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a 
                href="https://www.google.com/search?q=In+Smart+Media+Solapur"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 p-3 rounded-full hover:bg-blue-600 transition-colors"
                aria-label="Google Business Profile"
              >
                <Search size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <a 
                  href="#home"
                  onClick={(event) => scrollToSection(event, 'home')}
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                >
                  Home
                </a>
              </li>
              <li>
                <a 
                  href="#about"
                  onClick={(event) => scrollToSection(event, 'about')}
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                >
                  About Us
                </a>
              </li>
              <li>
                <a 
                  href="#services"
                  onClick={(event) => scrollToSection(event, 'services')}
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                >
                  Services
                </a>
              </li>
              <li>
                <a 
                  href="#why-us"
                  onClick={(event) => scrollToSection(event, 'why-us')}
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                >
                  Why Choose Us
                </a>
              </li>
              <li>
                <a 
                  href="#contact"
                  onClick={(event) => scrollToSection(event, 'contact')}
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Our Services</h3>
            <ul className="space-y-2 text-gray-400">
              <li>E-Rickshaw Branding</li>
              <li>Street Hoarding</li>
              <li>Social Media Marketing</li>
              <li>Radio/TV Advertising</li>
              <li>Print Media</li>
              <li>Digital Marketing</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="text-blue-400 mt-1 flex-shrink-0" size={18} />
                <div>
                  {companyInfo.phone.map((number, index) => (
                    <a 
                      key={index}
                      href={`tel:${number}`}
                      className="text-gray-400 hover:text-blue-400 block"
                    >
                      {number}
                    </a>
                  ))}
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="text-blue-400 mt-1 flex-shrink-0" size={18} />
                <div>
                  <a 
                    href={`mailto:${companyInfo.email}`}
                    className="text-gray-400 hover:text-blue-400 break-all block"
                  >
                    {companyInfo.email}
                  </a>
                  <a 
                    href={`mailto:${companyInfo.emailSecondary}`}
                    className="text-gray-400 hover:text-blue-400 break-all block"
                  >
                    {companyInfo.emailSecondary}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="text-blue-400 mt-1 flex-shrink-0" size={18} />
                <span className="text-gray-400">{companyInfo.address}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-400 mt-1 flex-shrink-0">🌐</span>
                <a 
                  href={`https://${companyInfo.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-400"
                >
                  {companyInfo.website}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 border-t border-gray-800">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-gray-400 text-sm">
            <span>{new Date().getFullYear()} In Smart Media. All rights reserved.</span>
          </div>
          <button
            onClick={scrollToTop}
            className="bg-blue-600 hover:bg-blue-700 p-3 rounded-full transition-colors"
          >
            <ArrowUp size={20} />
          </button>
        </div>
      </div>
    </footer>
  );
};
